import { Component, inject, signal, computed, OnInit, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DemandesService, Demande } from '../../../../core/service/demandes-service/demandes.service';
import { AuthService } from '../../../../core/service/auth/auth.service';
import { TitleService } from '../../../../core/service/title-service/title.service';
import { PermissionService } from '../../../../core/service/permission/permission.service';
import { SeasonsService } from '../../../../core/service/seasons-service/seasons.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-demandes-list',
  standalone: false,
  templateUrl: './demandes-list.component.html',
})
export class DemandesListComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private demandesService = inject(DemandesService);
  private seasonsService = inject(SeasonsService);
  private titleService = inject(TitleService);
  private destroyRef = inject(DestroyRef);
  permService = inject(PermissionService);
  private searchSubject = new Subject<string>();

  demandes = signal<Demande[]>([]);
  isLoading = signal(true);
  isPageLoading = signal(false);
  searchQuery = signal('');
  entityType = signal<string>('player');
  statusFilter = signal<string>('');
  selectedSeasonId = signal<number>(0);
  pagination = signal({ page: 1, limit: 10, total: 0, totalPages: 0 });
  stats = signal<any>({});

  entityTypes = [
    { value: 'player', label: 'Joueurs' },
    { value: 'manager', label: 'Dirigeants' },
    { value: 'staff', label: 'Staff technique' },
    { value: 'referee', label: 'Arbitres' },
    { value: 'club', label: 'Clubs' },
  ];

  statusOptions = [
    { value: '',               label: 'Tous' },
    { value: 'PENDING',        label: 'En attente' },
    { value: 'CLUB_ACCEPTED',  label: 'Accepté par le club' },
    { value: 'FED_VALIDATED',  label: 'Validé par la fédération' },
    { value: 'FED_REJECTED',   label: 'Rejeté par la fédération' },
    { value: 'REJECTED_CLUB',  label: 'Refusé par le club' },
    { value: 'CANCELLED',      label: 'Annulé' },
    { value: 'LICENCE_GENERATED', label: 'Licence générée' },
  ];

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  ngOnInit(): void {
    this.titleService.setPageTitle('Demandes');
    this.searchSubject.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.loadDemandes());
    this.seasonsService.getActive()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success && res.data?.sportseason_id) {
            this.selectedSeasonId.set(res.data.sportseason_id);
          }
          this.loadDemandes();
        },
        error: () => this.loadDemandes(),
      });
    this.loadStats();
  }

  loadDemandes(): void {
    this.isPageLoading.set(true);
    const body = {
      search: this.searchQuery(),
      status: this.statusFilter(),
      season_id: this.selectedSeasonId() || undefined,
      page: this.pagination().page,
      limit: this.pagination().limit,
    };
    this.demandesService.list(this.entityType(), body)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.demandes.set(res.data || []);
          if (res.pagination) {
            this.pagination.set(res.pagination);
          } else {
            const total = res.total || 0;
            this.pagination.set({
              page: body.page,
              limit: body.limit,
              total,
              totalPages: Math.ceil(total / body.limit),
            });
          }
          this.isPageLoading.set(false);
          this.isLoading.set(false);
        },
        error: () => {
          this.isPageLoading.set(false);
          this.isLoading.set(false);
        },
      });
  }

  loadStats(): void {
    this.demandesService.getStats()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.stats.set(res.stats || {});
        },
      });
  }

  onEntityTypeChange(type: string): void {
    this.entityType.set(type);
    this.pagination.update(p => ({ ...p, page: 1 }));
    this.loadDemandes();
  }

  onStatusFilterChange(status: string): void {
    this.statusFilter.set(status);
    this.pagination.update(p => ({ ...p, page: 1 }));
    this.loadDemandes();
  }

  onSearch(query: string): void {
    this.searchQuery.set(query);
    this.searchSubject.next(query);
  }

  goToPage(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.pagination.update(p => ({ ...p, page }));
    this.loadDemandes();
  }

  viewDetail(demande: Demande): void {
    this.router.navigate(['/Demandes', this.entityType(), demande.demande_id]);
  }

  getStatusClass(status: string): { text: string; bg: string; dot: string } {
    switch (status) {
      case 'PENDING':           return { text: '#d97706', bg: '#fef3c7', dot: '#d97706' };
      case 'CLUB_ACCEPTED':     return { text: '#0284c7', bg: '#e0f2fe', dot: '#0284c7' };
      case 'FED_VALIDATED':     return { text: '#16a34a', bg: '#dcfce7', dot: '#16a34a' };
      case 'FED_REJECTED':      return { text: '#dc2626', bg: '#fee2e2', dot: '#dc2626' };
      case 'REJECTED_CLUB':     return { text: '#b91c1c', bg: '#fee2e2', dot: '#b91c1c' };
      case 'CANCELLED':         return { text: '#78716c', bg: '#f5f5f4', dot: '#a8a29e' };
      case 'LICENCE_GENERATED': return { text: '#15803d', bg: '#bbf7d0', dot: '#15803d' };
      default:                  return { text: '#78716c', bg: '#f5f5f4', dot: '#a8a29e' };
    }
  }

  getStatusLabel(status: string): string {
    const opt = this.statusOptions.find(o => o.value === status);
    return opt?.label || status || '\u2014';
  }

  async validateDemande(demande: Demande): Promise<void> {
    const result = await SwalUtils.confirmGreen('Valider cette demande ?', 'Cette action est irr\u00e9versible.', 'Valider');
    if (!result.isConfirmed) return;

    this.demandesService.validate(this.entityType(), { demande_id: demande.demande_id })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            SwalUtils.success('Succ\u00e8s', 'Demande valid\u00e9e');
            this.loadDemandes();
            this.loadStats();
          }
        },
        error: (err: any) => SwalUtils.apiError(err),
      });
  }

  async rejectDemande(demande: Demande): Promise<void> {
    const { isConfirmed, motif } = await SwalUtils.confirmWithMotif('Rejeter cette demande ?', 'Rejeter');
    if (!isConfirmed) return;

    this.demandesService.reject(this.entityType(), { demande_id: demande.demande_id, motif })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) {
            SwalUtils.success('Succ\u00e8s', 'Demande rejet\u00e9e');
            this.loadDemandes();
            this.loadStats();
          }
        },
        error: (err: any) => SwalUtils.apiError(err),
      });
  }
}
